import { Tag } from "antd";

import { formatDate } from "@src/utils/dateHelpers";

export const edgeLogsButtonText = (showEdgeLogs: boolean) =>
  `${showEdgeLogs ? "Stop" : "Start"} Edge Logs Stream`;

/**
 * Maps a log level number to a text representation.
 *
 * Example: 30 -> INFO
 */
export const levelToText = (level: number) => {
  switch (level) {
    case 30:
      return <Tag color="blue">INFO</Tag>;
    case 40:
      return <Tag color="orange">WARNING</Tag>;
    case 50:
      return <Tag color="red">ERROR</Tag>;
    default:
      return `UNKNOWN ${level.toString()}`;
  }
};

/**
 * The basic structure of a log object.
 */
interface LogObject {
  level: number;
  time: number;
  msg: string;
}

/**
 * Type guard for checking if an object is a LogObject.
 */
export const isLogObject = (log: unknown): log is LogObject =>
  typeof log === "object" &&
  log !== null &&
  "level" in log &&
  typeof log.level === "number" &&
  "time" in log &&
  typeof log.time === "number" &&
  "msg" in log &&
  typeof log.msg === "string";

/**
 * Parses a log string into an object with the following properties:
 * - number: The index of the log in the log list
 * - level: The log level as a string
 * - time: The time of the log as an ISO Date string
 * - message: The actual message of the log
 * - raw: The raw log string
 */
export const parseLog = (log: string, index: number) => {
  try {
    const parsedLog = JSON.parse(log) as unknown;
    if (!isLogObject(parsedLog)) {
      return {
        number: index + 1,
        level: levelToText(50),
        time: 1,
        message: "Invalid log format",
        raw: log,
      };
    }

    return {
      number: index + 1,
      level: levelToText(parseInt(parsedLog.level.toString())),
      time: formatDate(new Date(parsedLog.time)),
      message: parsedLog.msg,
      raw: log,
    };
  } catch (error: unknown) {
    return {
      number: index + 1,
      level: levelToText(50),
      time: 1,
      message: `Log format error: ${error instanceof Error ? error.message : String(error)}`,
      raw: log,
    };
  }
};
